import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, Menu, X } from "lucide-react";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Projects", path: "/projects" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header
        data-testid="header"
        className={`sticky-header px-4 md:px-6 lg:px-8 py-4 ${
          isScrolled ? "scrolled bg-white/98" : "bg-transparent"
        }`}
      >
        <div className="container mx-auto max-w-7xl flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <span className="font-heading text-2xl md:text-3xl font-bold text-[#2D5A27]">
              CasaCurate
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-[#2D5A27] ${
                  isActive(link.path)
                    ? "text-[#2D5A27] font-semibold"
                    : "text-[#1A2C22]"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+17711871113"
              data-testid="header-phone"
              className="flex items-center gap-2 text-[#1A2C22] hover:text-[#2D5A27] transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">+1-771-1871-113</span>
            </a>
            <Link to="/contact">
              <Button
                data-testid="header-cta"
                className="bg-[#C2410C] hover:bg-[#9A3412] text-white h-11 px-6 rounded-full cta-accent"
              >
                Get Free Estimate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-[#1A2C22]"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}
      >
        <div className="p-6 pt-20">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={`text-lg font-medium py-3 border-b border-gray-100 ${
                  isActive(link.path) ? "text-[#2D5A27]" : "text-[#1A2C22]"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>
          <div className="mt-8 space-y-4">
            <a
              href="tel:+17711871113"
              data-testid="mobile-phone"
              className="flex items-center gap-3 text-[#1A2C22] py-3"
            >
              <Phone className="w-5 h-5 text-[#2D5A27]" />
              <span className="font-medium">+1-771-1871-113</span>
            </a>
            <Link to="/contact" className="block">
              <Button
                data-testid="mobile-cta"
                className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white h-12 rounded-full"
              >
                Get Free Estimate
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
